import { useEffect, useState, useCallback } from "react";
import { CalendarRange, Trash2, Plus, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const today = () => new Date().toISOString().split("T")[0];

export const AdminRates = ({ rooms }) => {
  const available = rooms || [];
  const [rules, setRules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ room_id: "", label: "", start: today(), end: today(), rate_type: "absolute", value: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(() => api.get("/rate-rules").then(({ data }) => setRules(data)).catch(() => setRules([])), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!form.room_id && available[0]) set("room_id", available[0].room_id); }, [available, form.room_id]);

  const roomName = (id) => rooms.find((r) => r.room_id === id)?.name || id;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/rate-rules", { ...form, value: Number(form.value) });
      toast.success("Seasonal rate added");
      setForm((f) => ({ ...f, label: "", value: "" }));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add rate");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/rate-rules/${id}`); toast.success("Rate removed"); load(); }
    catch { toast.error("Could not remove rate"); }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-5 lg:p-6" data-testid="admin-rates-section">
      <div className="flex items-center gap-2 mb-4">
        <CalendarRange className="h-5 w-5 text-primary" />
        <h2 className="font-serif text-xl font-semibold">Seasonal Rates</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Set special nightly pricing for date ranges. Each night in a booking uses the matching rule, else the room's base rate. Overlapping ranges for the same room aren't allowed.</p>

      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end mb-5">
        <div className="lg:col-span-2">
          <Label className="text-xs">Room</Label>
          <select value={form.room_id} onChange={(e) => set("room_id", e.target.value)} required data-testid="rate-room-select"
            className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
            {available.map((r) => <option key={r.room_id} value={r.room_id}>{r.name} (base ₹{r.price_per_night})</option>)}
          </select>
        </div>
        <div><Label className="text-xs">From</Label>
          <Input type="date" min={today()} value={form.start} onChange={(e) => set("start", e.target.value)} required data-testid="rate-start-input" /></div>
        <div><Label className="text-xs">To</Label>
          <Input type="date" min={form.start} value={form.end} onChange={(e) => set("end", e.target.value)} required data-testid="rate-end-input" /></div>
        <div><Label className="text-xs">Type</Label>
          <select value={form.rate_type} onChange={(e) => set("rate_type", e.target.value)} data-testid="rate-type-select"
            className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
            <option value="absolute">₹ / night</option>
            <option value="percent">% adjust</option>
          </select></div>
        <div><Label className="text-xs">{form.rate_type === "absolute" ? "Price ₹" : "Percent (e.g. 20 or -10)"}</Label>
          <Input type="number" value={form.value} onChange={(e) => set("value", e.target.value)} required placeholder={form.rate_type === "absolute" ? "2200" : "20"} data-testid="rate-value-input" /></div>
        <div className="sm:col-span-2 lg:col-span-1">
          <Label className="text-xs">Label (optional)</Label>
          <Input value={form.label} onChange={(e) => set("label", e.target.value)} placeholder="Diwali" data-testid="rate-label-input" /></div>
        <Button type="submit" disabled={saving} className="gap-1.5 bg-primary hover:bg-primary/90 lg:col-span-1" data-testid="rate-create-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
        </Button>
      </form>

      <div className="space-y-2">
        {rules.length === 0 && <p className="text-sm text-muted-foreground" data-testid="rates-empty">No seasonal rates yet — the base rate applies to every date.</p>}
        {rules.map((r) => (
          <div key={r.id} className="flex items-center justify-between rounded-lg border border-border bg-muted/40 px-4 py-2.5 text-sm" data-testid={`rate-row-${r.id}`}>
            <div>
              <span className="font-medium">{roomName(r.room_id)}</span>
              <span className="text-muted-foreground"> · {r.start} → {r.end}</span>
              {r.label && <span className="ml-1 text-xs rounded-full bg-secondary/15 text-secondary px-2 py-0.5">{r.label}</span>}
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono font-semibold text-secondary">
                {r.rate_type === "absolute" ? `₹${r.value}/night` : `${r.value > 0 ? "+" : ""}${r.value}%`}
              </span>
              <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-destructive" data-testid={`rate-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
